import React from 'react';
import { FiPlus } from 'react-icons/fi';
import styled from 'styled-components';
import { useAskVantage } from '../../context/AskVantageProvider';
import { useTheme } from '../../theme';
import Tooltip from '../Tooltip';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../state/store';
import { useMedia } from 'react-use';

const Sidebar: React.FC = () => {
  const {
    sidebarOpen,
    toggleSidebar,
    conversations,
    activeConversationId,
    selectConversation,
    createConversation,
    isConversationsLoading,
    isConversationsLoadingError
  } = useAskVantage();
  const theme = useTheme();
  const companyDetails = useSelector((state: RootState) => state.company?.company_details);
  const hasMaintenanceBanner = companyDetails?.maintenance_mode?.enabled || false;
  const isMobile = useMedia('(max-width: 768px)');

  // Group conversations by date
  const groupedConversations = React.useMemo(() => {
    const groups: { [key: string]: typeof conversations } = {
      Today: [],
      Yesterday: [],
      'Last 7 Days': [],
      Older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    conversations.forEach((conversation) => {
      const conversationDate = new Date(conversation.created_at);
      const conversationDay = new Date(
        conversationDate.getFullYear(),
        conversationDate.getMonth(),
        conversationDate.getDate()
      );

      if (conversationDay.getTime() === today.getTime()) {
        groups.Today.push(conversation);
      } else if (conversationDay.getTime() === yesterday.getTime()) {
        groups.Yesterday.push(conversation);
      } else if (conversationDate >= weekAgo) {
        groups['Last 7 Days'].push(conversation);
      } else {
        groups.Older.push(conversation);
      }
    });

    // Sort conversations within each group by created_at descending (most recent first)
    Object.keys(groups).forEach((groupKey) => {
      groups[groupKey].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });

    return groups;
  }, [conversations]);

  const handleNewConversation = () => {
    createConversation();
    if (isMobile) {
      toggleSidebar();
    }
  };

  const handleItemClick = (conversationId: string) => {
    selectConversation(conversationId);
    if (isMobile) {
      toggleSidebar();
    }
  };

  if (!sidebarOpen) {
    return <SidebarWrapper isOpen={false} $theme={theme} />;
  }

  return (
    <>
      <SidebarOverlay isOpen={sidebarOpen} onClick={toggleSidebar} />
      <SidebarWrapper
        isOpen={sidebarOpen}
        $theme={theme}
        style={{
          top: hasMaintenanceBanner ? '120px' : '60px',
          height: `calc(100vh - ${hasMaintenanceBanner ? '120px' : '60px'})`
        }}
      >
        <SidebarContent>
          <Header $theme={theme}>
            <Title $theme={theme}>History</Title>
            <Tooltip content="New conversation" position="bottom">
              <NewIconButton onClick={handleNewConversation} $theme={theme}>
                <FiPlus size={20} />
              </NewIconButton>
            </Tooltip>
          </Header>

          <ScrollContainer>
            {isConversationsLoading ? (
              <LoadingState $theme={theme}>
                <LoadingSpinner $theme={theme} />
                <LoadingText $theme={theme}>Loading conversations...</LoadingText>
              </LoadingState>
            ) : isConversationsLoadingError ? (
              <ErrorState $theme={theme}>
                <ErrorIcon>⚠️</ErrorIcon>
                <ErrorText $theme={theme}>{isConversationsLoadingError}</ErrorText>
              </ErrorState>
            ) : (
              <>
                {Object.entries(groupedConversations).map(([groupName, groupConversations]) => {
                  if (groupConversations.length === 0) return null;

                  return (
                    <Section key={groupName}>
                      <GroupHeading $theme={theme}>{groupName}</GroupHeading>
                      <ConversationList>
                        {groupConversations.map((conversation) => (
                          <ConversationItem
                            key={conversation.id}
                            data-active={
                              activeConversationId === conversation.id ? 'true' : undefined
                            }
                            isActive={activeConversationId === conversation.id}
                            onClick={() => handleItemClick(conversation.id)}
                            $theme={theme}
                          >
                            <ConversationTitle>
                              {conversation.title || 'New Conversation'}
                            </ConversationTitle>
                          </ConversationItem>
                        ))}
                      </ConversationList>
                    </Section>
                  );
                })}

                {(!conversations || conversations.length === 0) && (
                  <EmptyState $theme={theme}>No conversations yet</EmptyState>
                )}
              </>
            )}
          </ScrollContainer>
        </SidebarContent>
      </SidebarWrapper>
    </>
  );
};

// Styled Components
const SidebarOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1002;
  display: ${(props) => (props.isOpen ? 'block' : 'none')};

  @media (min-width: 769px) {
    display: none;
  }
`;

const SidebarWrapper = styled.div<{
  isOpen: boolean;
  $theme: any;
}>`
  position: fixed;
  left: 0;
  background-color: ${(props) => props.$theme.colors.containerBg};
  z-index: 1003;
  display: flex;
  flex-direction: column;
  transition: transform ${(props) => props.$theme.tokens.transitionFast};
  font-size: 16px; /* Override global font-size */

  /* Width and transform based on viewport and state */
  @media (min-width: 1281px) {
    width: 320px;
    transform: translateX(${(props) => (props.isOpen ? '0' : '-100%')});
  }

  @media (max-width: 1280px) and (min-width: 769px) {
    width: 280px;
    transform: translateX(${(props) => (props.isOpen ? '0' : '-100%')});
  }

  @media (max-width: 768px) {
    width: 320px;
    transform: translateX(${(props) => (props.isOpen ? '0' : '-100%')});
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div<{ $theme: any }>`
  padding: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2<{ $theme: any }>`
  margin: 0;
  font-size: 18px; /* Fixed size instead of rem to override global styles */
  font-weight: 600;
  color: ${(props) => props.$theme.colors.sidebarTitleColor};
  user-select: none;

  @media (max-width: 480px) {
    font-size: 17px;
  }

  @media (max-width: 375px) {
    font-size: 16px;
  }
`;

const NewIconButton = styled.button<{ $theme: any }>`
  background: none;
  color: ${(props) => props.$theme.colors.sidebarItemColor};
  border: none;
  padding: 8px;
  border-radius: ${(props) => props.$theme.tokens.radiusMd};
  cursor: pointer;
  transition: all ${(props) => props.$theme.tokens.transitionFast};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${(props) => props.$theme.colors.mainBg};
    transform: scale(1.05);
  }
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const GroupHeading = styled.div<{ $theme: any }>`
  color: ${(props) => props.$theme.colors.sidebarGroupColor};
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  user-select: none;

  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 10px;
  }

  @media (max-width: 375px) {
    font-size: 0.75rem;
    margin-bottom: 8px;
    letter-spacing: 0.3px;
  }
`;

const ConversationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ConversationItem = styled.div<{
  isActive: boolean;
  $theme: any;
}>`
  color: ${(props) => props.$theme.colors.sidebarItemColor};
  font-size: 0.9rem;
  padding: 12px 16px;
  border-radius: ${(props) => props.$theme.tokens.radiusMd};
  cursor: pointer;
  transition: all ${(props) => props.$theme.tokens.transitionFast};
  line-height: 1.4;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  color: ${(props) => props.$theme.colors.sidebarItemColor};
  background-color: transparent;

  ${(props) =>
    props.isActive &&
    `
    border-left: 2px solid ${props.$theme.tokens.primary};
    background-color: rgba(50, 50, 50, 0.06);
    padding-left: 14px;
    color: ${props.$theme.colors.primary};
  `}

  &:hover {
    transform: translateX(2px);
    background-color: rgba(50, 50, 50, 0.06);
  }

  .delete-btn {
    opacity: 0;
    pointer-events: none;
    transition: opacity ${(props) => props.$theme.tokens.transitionFast};
  }

  &:hover .delete-btn {
    opacity: 1;
    pointer-events: auto;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 10px 14px;
  }

  @media (max-width: 375px) {
    font-size: 0.8rem;
    padding: 9px 12px;
  }
`;

const ConversationTitle = styled.span`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  flex: 1;
  line-height: 1.3;
  word-break: break-word;
  user-select: none;
`;

const EmptyState = styled.div<{ $theme: any }>`
  text-align: center;
  padding: 40px 20px;
  color: ${(props) => props.$theme.colors.sidebarGroupColor};
  font-size: 0.9rem;
  font-style: italic;
`;

const LoadingState = styled.div<{ $theme: any }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
`;

const LoadingSpinner = styled.div<{ $theme: any }>`
  width: 40px;
  height: 40px;
  border: 3px solid ${(props) => props.$theme.colors.messageBoxBorder};
  border-top-color: ${(props) => props.$theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div<{ $theme: any }>`
  color: ${(props) => props.$theme.colors.sidebarGroupColor};
  font-size: 0.9rem;
`;

const ErrorState = styled.div<{ $theme: any }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 12px;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
`;

const ErrorText = styled.div<{ $theme: any }>`
  color: ${(props) => props.$theme.colors.sidebarItemColor};
  font-size: 0.9rem;
  text-align: center;
  line-height: 1.5;
`;

export default Sidebar;
